import apiFetch from "@/src/utils/apiFetch";

import {
  CardType,
  Medicine,
  medicineHoraires,
  medicineUnits,
  Ordonnance,
  OrdonnanceMedicine,
  OrdonnanceTemplate,
  Patient,
  PatientDocument,
  TaskDuration,
  taskDurations,
} from "@/src/data/allTypes";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";

import {
  closeDialog,
  closeDrawer,
  openConfirmDialog,
  openDialog,
  openDrawer,
} from "../../components/dialogs";

import { FaRegTrashAlt } from "react-icons/fa";

import { FolderItem } from "@/src/data/allTypes";
import { FaRegSave } from "react-icons/fa";
import { IoMdAdd } from "react-icons/io";
import { MdMoreVert } from "react-icons/md";
import { openPosologyCalculation } from "../medicines/posologyCalculationsHook";

import {
  MedicinesAndTemplatesChoiceList,
  MedicinesChoiceList,
} from "@/src/app/medicines/medicinesHook";
import { ReorderableItems } from "@/src/components/reorderable";

import { Button, IconButton } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import {
  FetchableList,
  ListConstructor,
} from "@/src/components/fetchableLists";
import { USER } from "@/src/data/globalVariables";
import {
  getUserId,
  getUserShortName,
  jsonAbstract,
  removeIds,
  uid,
  updateOrPush,
} from "@/src/utils/utils";
import { openDocumentForm } from "../documents/documentsHook";
import { OrdonnanceMedicineForm } from "../medicines/ordonnanceMedicineForm";
import {
  openOrdonnanceTemplateForm,
  OrdonnanceMedicineCard,
} from "../medicines/ordonnanceTemplateHook";

function OrdonnancesCompartment({
  color,
  patient,
  parent,
  onChange,
}: {
  color?: CardType;
  patient: Patient;
  parent: FolderItem | null | undefined;
  onChange: (newValue: Ordonnance) => void;
}) {
  const [reload, setReload] = useState(0);
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["OrdonnancesCompartment", patient.id, parent?.id, reload],
    queryFn: () =>
      apiFetch(
        "ordonnances",
        "loadMany",
        {
          categories: ["ambulatory"],
          patientId: patient.id,
          parentId: parent?.id,
        },
        "Erreur lors du chargement des ordonnances"
      ),
  });

  async function openMedicineAndTemplateChoice() {
    await openDrawer({
      id: "MedicinesAndTemplatesChoiceList",
      title: "Choisir un médicament ou un modèle",
      content: (
        <MedicinesAndTemplatesChoiceList
          showRenewButton={true}
          parent={parent}
          patient={patient}
          onSelect={async (medicine, template) => {
            if (template) await createOrdonnanceFromTemplate(template);
            if (medicine) await createOrdonnanceFromMedicine(medicine);
            closeDrawer("MedicinesAndTemplatesChoiceList");
          }}
        ></MedicinesAndTemplatesChoiceList>
      ),
    });
  }

  async function createOrdonnanceFromTemplate(template: OrdonnanceTemplate) {
    let ordonnance: Ordonnance = {
      patient,
      parent,
      creator: USER.get()!,
      category: "ambulatory",
      ordonnanceMedicines: removeIds(template.ordonnanceMedicines || []),
    };

    await openPosologyCalculation({
      ordonnance,
      onConfirm: async () => {
        await apiFetch(
          "ordonnances",
          "addOne",
          { ordonnance },
          "Erreur lors de l'enregistrement"
        );
        closeDrawer("ordonnanceTemplateDrawer");
        setReload((prev) => prev + 1);
      },
    });
  }

  async function createOrdonnanceFromMedicine(medicine: Medicine) {
    let ordonnanceMedicine: OrdonnanceMedicine = {
      medicine,
      patient,
      creator: USER.get()!,
      intakes: [],
      tasks: [],
      notes: "",
      duration: "7 jours",
    };

    await openDrawer({
      title:
        `${ordonnanceMedicine?.medicine?.name} - ${
          ordonnanceMedicine?.medicine?.dci || ""
        }` || "Ajouter un médicament",
      content: (
        <OrdonnanceMedicineForm
          isTemplate={false}
          category="ambulatory"
          current={ordonnanceMedicine}
          onChange={(newValue) => {
            ordonnanceMedicine = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        let ordonnance: Ordonnance = {
          patient,
          parent,
          creator: USER.get()!,
          category: "ambulatory",
          ordonnanceMedicines: [ordonnanceMedicine],
        };
        let result = await apiFetch(
          "ordonnances",
          "addOne",
          { ordonnance },
          "Erreur lors de l'enregistrement"
        );

        let medicine = ordonnanceMedicine.medicine;
        await apiFetch("medicines", "addOne", {
          medicine: {
            id: medicine.id,
            creator: getUserId(),
            defaultRoute: ordonnanceMedicine.intakes?.[0]?.route,
            defaultUnit: ordonnanceMedicine.intakes?.[0]?.unit,
          } as Medicine,
        });
        if (!result) return;
        onChange(result);
        closeDrawer("MedicinesAndTemplatesChoiceList");
        setReload((prev) => prev + 1);
      },
    });
  }

  if (!data?.length) {
    return (
      <CompartmentCard
        title={"Ordonnances"}
        color={color || "purple"}
        onClick={async () => {
          await openMedicineAndTemplateChoice();
        }}
      >
        <div
          className="center grid c1 gap10 p20 gray"
          style={{ minHeight: "300px" }}
        >
          <div className="grid c1 gap10">
            <div>Aucune ordonnance ajoutée</div>
            <Button
              color={color || "purple"}
              onClick={async () => {
                await openMedicineAndTemplateChoice();
              }}
            >
              Nouvelle ordonnance
            </Button>
            {/* <OpenFicheTherapeutiqueButton
              patient={patient}
              parent={parent!}
              color={color}
            /> */}
          </div>
        </div>
      </CompartmentCard>
    );
  }

  return (
    <ListConstructor
      data={data}
      isLoading={isLoading}
      isError={isError}
      error={error}
    >
      <div className="grid c1 gap20">
        {data?.map((ordonnance, index) => {
          return (
            <OrdonnanceContainer
              type={color}
              patient={patient}
              key={uid()}
              index={index + 1}
              currentOrdonnance={ordonnance}
              onNewOrdonnanceClick={async () => {
                openMedicineAndTemplateChoice();
              }}
              onDelete={async () => {
                setReload((prev) => prev + 1);
              }}
            />
          );
        })}
      </div>
    </ListConstructor>
  );
}

function OrdonnanceContainer({
  patient,
  parent,
  index,
  currentOrdonnance,
  onNewOrdonnanceClick,
  onDelete,
  type,
}: {
  patient: Patient;
  parent?: FolderItem;
  index?: number;
  currentOrdonnance: Ordonnance;
  onNewOrdonnanceClick?: () => void;
  onChange?: (newValue: Ordonnance) => void;
  onDelete?: () => void;
  type?: CardType;
}) {
  const [pendingItem, setPendingItem] = useState<OrdonnanceMedicine | null>(
    null
  );
  let [ordonnance, setOrdonnance] = useState(currentOrdonnance);

  async function openOrdonnanceMedicineForm(
    ordonnanceMedicine: OrdonnanceMedicine
  ) {
    await openDrawer({
      title:
        `${ordonnanceMedicine?.medicine?.name} - ${
          ordonnanceMedicine?.medicine?.dci || ""
        }` || "Ajouter un médicament",
      content: (
        <OrdonnanceMedicineForm
          isTemplate={false}
          category="ambulatory"
          current={ordonnanceMedicine}
          onChange={(newValue) => {
            ordonnanceMedicine = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        ordonnance.ordonnanceMedicines = updateOrPush(
          ordonnance.ordonnanceMedicines,
          ordonnanceMedicine
        );

        await openPosologyCalculation({
          ordonnance,
          onConfirm: async () => {
            let result = await apiFetch(
              "ordonnances",
              "addOne",
              { ordonnance },
              "Erreur lors de l'enregistrement"
            );
            if (!result) return;
            ordonnance = result;
            closeDrawer("medicineChoiceDrawer");
            setOrdonnance({ ...ordonnance });
          },
        });
      },
    });
  }

  async function openMedicineChoiceDrawer() {
    await openDrawer({
      id: "medicineChoiceDrawer",
      title: "Choisir un médicament",
      content: (
        <MedicinesChoiceList
          key={uid()}
          onSelect={async (medicine) => {
            let ordonnanceMedicine: OrdonnanceMedicine = {
              medicine,
              patient,
              creator: USER.get()!,
              ordonnance: jsonAbstract(ordonnance.id),
              intakes: [],
              tasks: [],
              notes: "",
              duration: "7 jours",
            };

            await openOrdonnanceMedicineForm(ordonnanceMedicine);
          }}
        />
      ),
    });
  }

  function generateOrdonnanceHtml({ ordonnance }: { ordonnance: Ordonnance }) {
    let html = ``;
    let index = 0;
    for (let ordonnanceMedicine of ordonnance.ordonnanceMedicines || []) {
      let medicine = ordonnanceMedicine?.medicine;
      if (!medicine) continue;

      let intakesHtml = ``;
      for (let intake of ordonnanceMedicine?.intakes || []) {
        if (!intake) continue;
        const {
          duration,
          dilutionVolume,
          solution,
          posology,
          unit,
          horaire,
          route,
          notes,
        } = intake;
        const horaireInfo = medicineHoraires[horaire];
        const unitInfo = medicineUnits[unit];
        const taskDurationInfo =
          taskDurations[intake?.duration as TaskDuration];

        intakesHtml += `<strong>${posology} ${
          unitInfo?.shortName || ""
        }</strong>  ${horaireInfo?.name || ""} par voie ${route || ""} ${
          notes || ""
        } \n`;
      }
      html += `<div class="grid gap10" style="grid-template-columns:2fr 2fr 1fr;">
          <div class="capitalize">${index + 1}- <strong>${
        medicine.name
      }</strong> <span class="gray">${medicine.dci || ""}</span></div>
          <div>${intakesHtml}</div>
          <div>pendant ${ordonnanceMedicine?.duration || "-"}</div>
      </div>\n\n`;
      index++;
    }

    return html;
  }

  async function openPrintableOrdonnance(ordonnance: Ordonnance) {
    let patientDocument: PatientDocument = {
      title: "Ordonnance",
      content: generateOrdonnanceHtml({ ordonnance }),
      status: "pending",
      patient,
      parent,
      template: {
        content: generateOrdonnanceHtml({ ordonnance }),
        title: "Ordonnance",
        showTitle: true,
        showHeader: true,
        showLeftBar: true,
        barcodeLocation: "none",
        showPatientIdentity: true,
        creator: USER?.get(),
        format: "a4",
        isValidatable: false,
        name: "Ordonnance",
        parent: null,
      },
      creator: USER?.get(),
      refAnswer: null,
    };

    await openDocumentForm({
      patient,
      patientDocument,
    });
  }

  async function deleteOrdonnance() {
    await apiFetch(
      "general",
      "deleteAny",
      {
        id: ordonnance.id,
        table: "ordonnances",
      },
      "Erreur lors de la suppression"
    );
    closeDialog("ordonnanceActionsDrawer");

    onDelete?.();
  }

  return (
    <CompartmentCard
      color={type || "purple"}
      title={
        <div
          className="grid gap10 acenter"
          style={{ gridTemplateColumns: "auto auto 1fr" }}
        >
          <span>Ordonnance {index || ""}</span>
          <IconButton
            onClick={async () => {
              await openDialog({
                id: "ordonnanceActionsDrawer",
                title: "Actions",
                content: (
                  <div className="c1 gap10">
                    <Button
                      icon={<FaRegSave size={18} />}
                      onClick={async () => {
                        let template: OrdonnanceTemplate = {
                          name: "",
                          creator: getUserId(),
                          category: "ambulatory",
                          ordonnanceMedicines:
                            ordonnance.ordonnanceMedicines.map(
                              (ordonnanceMedicine) => {
                                return {
                                  ...jsonAbstract(ordonnanceMedicine),
                                  id: null,
                                } as OrdonnanceMedicine;
                              }
                            ),
                        };

                        template = await openOrdonnanceTemplateForm({
                          template,
                          onConfirm: async (template) => {
                            closeDialog("ordonnanceActionsDrawer");
                          },
                        });
                      }}
                    >
                      Enregistrer comme modèle
                    </Button>

                    <Button
                      type={"tertiary"}
                      icon={<IoMdAdd size={18} />}
                      onClick={async () => {
                        await onNewOrdonnanceClick?.();
                        closeDialog("ordonnanceActionsDrawer");
                      }}
                    >
                      Créer une {(index || 0) + 1}ème ordonnance
                    </Button>
                    <Button
                      type={"tertiary"}
                      icon={<FaRegTrashAlt size={18} />}
                      onClick={() => {
                        openConfirmDialog({
                          content: `Voulez-vous vraiment supprimer cette ordonnance ?`,
                          onConfirm: async () => {
                            await deleteOrdonnance();
                          },
                        });
                      }}
                    >
                      Supprimer
                    </Button>
                  </div>
                ),
              });
            }}
          >
            <MdMoreVert size={18} />
          </IconButton>
        </div>
      }
      secondaryButtonTitle="Imprimer"
      onSecondaryClick={() => {
        openPrintableOrdonnance(ordonnance);
      }}
      onClick={async () => {
        await openMedicineChoiceDrawer();
      }}
    >
      <ListConstructor data={ordonnance?.ordonnanceMedicines} isLoading={false}>
        <div className="grid c1 gap10">
          <ReorderableItems
            items={ordonnance?.ordonnanceMedicines || []}
            listId={`ordonnanceMedicines ${ordonnance.id}`}
            listType={"ordonnanceMedicines"}
            onReorder={async (items, oldIndex, newIndex, movedItem) => {
              ordonnance.ordonnanceMedicines = items;
              setPendingItem(movedItem);
              await apiFetch(
                "ordonnances",
                "addOne",
                { ordonnance },
                "Erreur lors de l'enregistrement"
              );
              setOrdonnance({ ...ordonnance });

              // await setReload((prev) => prev + 1);;
            }}
            onRemove={async (removedItem) => {
              ordonnance.ordonnanceMedicines =
                ordonnance.ordonnanceMedicines?.filter(
                  (element) => element?.id != removedItem.id
                );

              await apiFetch(
                "ordonnances",
                "addOne",
                { ordonnance },
                "Erreur lors de l'enregistrement"
              );

              setOrdonnance({ ...ordonnance });
              if (ordonnance.ordonnanceMedicines?.length == 0)
                await deleteOrdonnance();
              // await setReload((prev) => prev + 1);;
            }}
          >
            {ordonnance?.ordonnanceMedicines?.map(
              (ordonnanceMedicine, index) => {
                if (!ordonnanceMedicine) return null;
                return (
                  <OrdonnanceMedicineCard
                    key={uid()}
                    type={type}
                    ordonnanceMedicine={ordonnanceMedicine}
                    onClick={async () => {
                      await openOrdonnanceMedicineForm(ordonnanceMedicine);
                    }}
                    onDelete={async () => {
                      ordonnance.ordonnanceMedicines?.splice(index, 1);
                      await apiFetch(
                        "ordonnances",
                        "addOne",
                        { ordonnance },
                        "Erreur lors de l'enregistrement"
                      );
                      if (ordonnance.ordonnanceMedicines?.length == 0)
                        await deleteOrdonnance();

                      setOrdonnance({ ...ordonnance });
                    }}
                  ></OrdonnanceMedicineCard>
                );
              }
            )}
          </ReorderableItems>
        </div>
      </ListConstructor>
    </CompartmentCard>
  );
}

function OrdonnancesHistory({
  patient,
  parent,
  type,
}: {
  patient: Patient;
  parent: FolderItem | null | undefined;
  type?: CardType;
}) {
  const [reload, setReload] = useState(0);
  return (
    <CompartmentCard
      color={type || "purple"}
      title={"Ordonnances"}
      onClick={async () => {
        await openDrawer({
          id: "ordonnanceDrawer",
          title: "Ajouter une ordonnance",
          content: (
            <OrdonnancesCompartment
              color={type}
              patient={patient}
              parent={{
                id: `ordonnanceParents:${uid()}`,
                confidentiality: null,
                creator: null,
                functionalSigns: [],
                patient,
                physicalSigns: [],
                readBy: [],
                type: "consultation",
                appointment: null,
              }}
              onChange={async () => {}}
            />
          ),
          onConfirm: async () => {
            setReload((prev) => prev + 1);
          },
        });
        setReload((prev) => prev + 1);
      }}
    >
      <FetchableList
        queryKey={["OrdonnancesHistory", patient.id, parent?.id, reload]}
        queryFn={() =>
          apiFetch(
            "ordonnances",
            "loadMany",
            {
              categories: ["ambulatory"],
              patientId: patient.id,
              parentId: null,
            },
            "Erreur lors du chargement des ordonnances"
          )
        }
      >
        {(data) => (
          <div className="c1 gap10">
            {data?.map((ordonnance, index) => {
              return (
                <OrdonnanceHistoryCard
                  key={uid()}
                  patient={patient}
                  ordonnance={ordonnance}
                  onChange={async (newValue) => {
                    setReload((prev) => prev + 1);
                  }}
                  onDelete={async () => {
                    setReload((prev) => prev + 1);
                  }}
                />
              );
            })}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

function OrdonnanceHistoryCard({
  patient,
  type,
  ordonnance,
  onDelete,
  onChange,
}: {
  patient: Patient;
  type?: CardType;
  ordonnance: Ordonnance;
  onDelete: () => void;
  onChange: (newValue: Ordonnance) => void;
}) {
  return (
    <Card
      color={type || "purple"}
      title={<div> Ordonnance du {moment(ordonnance._date).format("LL")}</div>}
      subTitle={getUserShortName(ordonnance.creator)}
      onClick={async () => {
        await openDrawer({
          id: "ordonnanceDrawer",
          title: "Ordonnance",
          content: (
            <OrdonnancesCompartment
              color={type}
              patient={patient}
              parent={ordonnance.parent}
              onChange={(newValue) => {
                onChange(newValue);
              }}
            />
          ),
        });
      }}
      onDelete={async () => {
        await openConfirmDialog({
          content: `Voulez-vous vraiment supprimer cette ordonnance ?`,
          onConfirm: async () => {
            await apiFetch(
              "general",
              "deleteAny",
              {
                id: ordonnance.id,
                table: "ordonnances",
              },
              "Erreur lors de la suppression"
            );
            onDelete();
          },
        });
      }}
    >
      <div className="grid c1 gap20">
        <div className="c1 gap5">
          {ordonnance?.ordonnanceMedicines?.map((ordonnanceMedicine, index) => {
            let medicine = ordonnanceMedicine?.medicine;
            if (!medicine) return null;
            return (
              <div key={uid()} className="ordonnanceLine grid gap10">
                <div>
                  <span className="">
                    {[
                      medicine.name,
                      medicine.dosage || null,
                      medicine.form || null,
                    ]
                      .filter((e) => e != null)
                      .join(" - ")}
                  </span>
                  <span className="gray">
                    - {ordonnanceMedicine?.medicine?.dci || ""}
                  </span>
                </div>
                <div>
                  {ordonnanceMedicine?.intakes?.map((intake) => {
                    if (!intake?.horaire) return;
                    let horaire = medicineHoraires[intake?.horaire];

                    return (
                      <div key={uid()}>
                        {intake?.posology || ""} {horaire?.name}{" "}
                        {intake?.notes || ""}
                      </div>
                    );
                  })}
                </div>
                {ordonnanceMedicine?.duration ? (
                  <div>Pendant {ordonnanceMedicine?.duration}</div>
                ) : (
                  <div className="br p5 redish">
                    Veuillez spécifier la durée
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

export {
  MedicinesAndTemplatesChoiceList,
  MedicinesChoiceList,
  OrdonnanceMedicineCard,
  OrdonnancesCompartment,
  OrdonnancesHistory,
};
